import { AbstractControl, ValidationErrors } from '@angular/forms';

export function digitsOnly(input: string | null | undefined): string {
  return (input ?? '').replace(/\D/g, '');
}

export function alphaNumericUpper(input: string | null | undefined): string {
  return (input ?? '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
}

export function normalizeIban(input: string | null | undefined): string {
  return alphaNumericUpper(input);
}

export function isNullOrWhiteSpace(value: unknown): boolean {
  return value === null || value === undefined || value.toString().trim() === '';
}

export function luhnValidate(value: string): boolean {
  const digits = digitsOnly(value);
  if (!digits) return false;

  let sum = 0;
  const parity = digits.length % 2;

  for (let i = 0; i < digits.length; i++) {
    let digit = parseInt(digits.charAt(i), 10);
    if (Number.isNaN(digit)) return false;

    if (i % 2 === parity) {
      digit *= 2;
    }

    if (digit > 9) {
      digit -= 9;
    }

    sum += digit;
  }

  return sum % 10 === 0;
}

export function luhnValidateTenDigits(value: string): boolean {
  const digits = digitsOnly(value);
  if (digits.length !== 10) return false;
  return luhnValidate(digits);
}

export function mergeErrors(
  current: ValidationErrors | null,
  toAdd: ValidationErrors
): ValidationErrors {
  return { ...(current ?? {}), ...toAdd };
}

export function removeError(
  current: ValidationErrors | null,
  errorKey: string
): ValidationErrors | null {
  if (!current || !current[errorKey]) return current;

  const clone = { ...current };
  delete clone[errorKey];
  return Object.keys(clone).length ? clone : null;
}

export function addControlError(
  control: AbstractControl | null,
  errorKey: string,
  errorValue: true = true
): void {
  if (!control) return;

  const errors = mergeErrors(control.errors, { [errorKey]: errorValue });
  control.setErrors(errors);
}

export function clearControlError(
  control: AbstractControl | null,
  errorKey: string
): void {
    if (!control) return;

    const errors = removeError(control.errors, errorKey);
    control.setErrors(errors);
}

export function formatIbanForDisplay(value: string | null | undefined): string {
  const normalized = normalizeIban(value);
  return normalized.replace(/(.{4})/g, '$1 ').trim();
}

export function normalizeSwedishClearing(value: string | null | undefined): string {
  return digitsOnly(value);
}

export function normalizeSwedishAccountNumber(value: string | null | undefined): string {
  return digitsOnly(value);
}

export function normalizeComparableName(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

const swedishCenturyPrefixes = ['16', '18', '19', '20'];

export function normalizeComparableOrgOrPersonalNumber(value: string | null | undefined): string {
  const digits = digitsOnly(value);

  if (digits.length !== 12 || !swedishCenturyPrefixes.includes(digits.substring(0, 2))) {
    return digits;
  }

  const withoutCenturyPrefix = digits.substring(2);
  return luhnValidateTenDigits(withoutCenturyPrefix) ? withoutCenturyPrefix : digits;
}
